import type { ReactNode } from 'react';

import { DesktopSearchBar } from 'components/DesktopSearchBar/DesktopSearchBar';
import { DesktopSearchBarLocationInput } from 'components/DesktopSearchBar/DesktopSearchBarLocationInput';
import { DesktopSearchBarQueryInput } from 'components/DesktopSearchBar/DesktopSearchBarQueryInput';
import { DesktopSearchBarSearchTypeSelect } from 'components/DesktopSearchBar/DesktopSearchBarSearchTypeSelect';
import { DesktopSearchBarSubmitButton } from 'components/DesktopSearchBar/DesktopSearchBarSubmitButton';
import { Dropdown } from 'components/Dropdown/Dropdown';
import { DropdownHeaderTrigger } from 'components/Dropdown/HeaderTrigger/DropdownHeaderTrigger';
import { Icon } from 'components/Icon/Icon';
import { MobileSearchMenu } from 'components/MobileSearchMenu/MobileSearchMenu';
import { PageHeader } from 'components/PageHeader/PageHeader';
import { PageHeaderOld } from 'components/PageHeader/PageHeaderOld';
import { CartDropdownButtonContainerLoadable } from 'containers/CartDropdownButton/CartDropdownButtonContainerLoadable';
import { FlashMessageContainer } from 'containers/FlashMessageContainer';
import { MyAccountDropdownContainerLoadable } from 'containers/MyAccountDropdown/MyAccountDropdownContainerLoadable';
import { NotificationsDropdownContainerLoadable } from 'containers/NotificationsDropdown/NotificationsDropdownContainerLoadable';
import { useEnvironment, useUser } from 'contexts/UserEnvironmentContext';
import { useAbExperimentBucket } from 'hooks/abExperiment/useAbExperimentBucket';
import { useLogin } from 'hooks/useLogin';
import { useI18n } from 'i18n/hooks/useI18n';
import type { SearchFromPageType } from 'modules/search/types/SearchFromPageType';
import type { SearchType } from 'modules/search/types/SearchType';
import { getDonateRoutePath } from 'routing/utils/getDonateRoutePath';
import { trackClicked } from 'utils/analytics/track/trackClicked';
import { showModal } from 'zustand-stores/modalStore';

import { pageHeaderContainerDonateTextByLocale } from './pageHeaderContainerDonateTextByLocale';
import { usePageHeaderContainerSearch } from './usePageHeaderContainerSearch';

type Props = {
  searchRefinements?: ReactNode;
  showDesktopSearch: boolean;
  defaultSearchType: SearchType;
  progressBar: ReactNode;
  isInSearchPage: boolean;
  isInSearchLandingPage: boolean;
  searchFromPageType?: SearchFromPageType;
};

export function PageHeaderContainer({
  searchRefinements,
  showDesktopSearch,
  defaultSearchType,
  progressBar,
  isInSearchPage,
  isInSearchLandingPage,
  searchFromPageType,
}: Props) {
  const {
    initialQuery,
    searchLocation,
    changeSearchLocation,
    search,
    searchOnClear,
  } = usePageHeaderContainerSearch({
    defaultSearchType,
    isInSearchPage,
    isInSearchLandingPage,
    searchFromPageType,
  });

  const { user } = useUser();
  const { environment } = useEnvironment();
  const { i18n } = useI18n();
  const [bucketValue] = useAbExperimentBucket('PAGE_HEADER_2025_6');

  // TODO remove check for EN once translations are available
  const PageHeaderComponent =
    bucketValue === 'NEW' && CURRENT_LOCALE() === 'en'
      ? PageHeader
      : PageHeaderOld;

  const { loginLink, onClickLogin } = useLogin();

  return (
    <PageHeaderComponent
      locale={CURRENT_LOCALE()}
      loginHref={loginLink}
      signupHref="/signup"
      isUserLoaded={Boolean(environment)}
      onClickLogin={onClickLogin}
      onClickSignup={(e) => {
        e.preventDefault();
        showModal('SIGNUP', {});
      }}
      donate={{
        href: getDonateRoutePath(CURRENT_LOCALE()),
        text: pageHeaderContainerDonateTextByLocale[CURRENT_LOCALE()],
      }}
      userDisplayName={user?.name ?? user?.email}
      orgs={user?.orgs ?? []}
      notificationsDropdown={<NotificationsDropdownContainerLoadable />}
      myAccountDropdown={<MyAccountDropdownContainerLoadable />}
      cartDropdownButton={<CartDropdownButtonContainerLoadable />}
      desktopSearchBar={
        showDesktopSearch && (
          <DesktopSearchBar
            defaultSearchType={defaultSearchType}
            onSearch={search}
            searchTypeSelect={<DesktopSearchBarSearchTypeSelect />}
            locationInput={
              <DesktopSearchBarLocationInput
                searchLocation={searchLocation ?? null}
                onChangeSearchLocation={changeSearchLocation}
              />
            }
            queryInput={
              <DesktopSearchBarQueryInput
                initialQuery={initialQuery}
                searchOnClear={searchOnClear}
              />
            }
            submitButton={<DesktopSearchBarSubmitButton />}
          />
        )
      }
      mobileSearchDropdown={
        <Dropdown
          trigger={
            <DropdownHeaderTrigger
              ariaLabel={i18n('Toggle search')}
              qaId="search-menu-button"
              variant="grey"
            >
              <Icon name="search" size={20} />
            </DropdownHeaderTrigger>
          }
          onToggle={(open) =>
            open && trackClicked('Header Utility CTA - Search')
          }
        >
          <MobileSearchMenu
            defaultSearchType={defaultSearchType}
            initialQuery={initialQuery}
            searchLocation={searchLocation}
            onChangeSearchLocation={changeSearchLocation}
            onSearch={search}
            searchOnClear={searchOnClear}
          />
        </Dropdown>
      }
      below={
        <>
          {searchRefinements}
          <FlashMessageContainer />
          {progressBar}
        </>
      }
    />
  );
}
